import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Star, CheckCircle, Quote, ThumbsUp, ExternalLink, MapPin } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ReviewsPage = () => {
  const [reviews, setReviews] = useState([]);
  const [googleReviews, setGoogleReviews] = useState([]);
  const [placeInfo, setPlaceInfo] = useState(null);
  const [stats, setStats] = useState({ 
    total: 0, 
    average: 0, 
    distribution: {},
    local_count: 0,
    google_count: 0,
    local_average: 0,
    google_average: 0,
    combined_average: 0
  });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [activeTab, setActiveTab] = useState("all"); // all, local, google
  const [formData, setFormData] = useState({
    customer_name: "",
    email: "",
    rating: 5,
    title: "",
    text: "",
    service_type: "general",
    vehicle_info: ""
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchCombinedReviews();
  }, []);

  const fetchCombinedReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/combined`);
      const data = response.data;
      
      // Alle Reviews
      const allReviews = data.reviews || [];
      const localReviews = allReviews.filter(r => r.source === "local");
      const googleRevs = allReviews.filter(r => r.source === "google");
      
      setReviews(localReviews);
      setGoogleReviews(googleRevs);
      setPlaceInfo(data.place_info || null);
      
      // Stats
      if (data.stats) {
        setStats({
          total: data.stats.total || 0,
          local_count: data.stats.local_count || 0,
          google_count: data.stats.google_count || 0,
          local_average: data.stats.local_average || 0,
          google_average: data.stats.google_average || 0,
          combined_average: data.stats.combined_average || 0,
          average: data.stats.combined_average || 0,
          distribution: data.stats.distribution || {}
        });
      }
    } catch (error) {
      console.error("Error fetching reviews:", error);
      // Fallback: nur lokale Reviews laden
      fetchLocalReviews();
    } finally {
      setLoading(false);
    }
  };

  const fetchLocalReviews = async () => {
    try {
      const [reviewsRes, statsRes] = await Promise.all([
        axios.get(`${API}/reviews?approved_only=true`),
        axios.get(`${API}/reviews/stats`)
      ]);
      setReviews(reviewsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching local reviews:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_name || !formData.title || !formData.text) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, formData);
      setSubmitted(true);
      toast.success("Vielen Dank für Ihre Bewertung!");
    } catch (error) {
      console.error("Error submitting review:", error);
      toast.error("Fehler beim Senden. Bitte versuchen Sie es erneut.");
    } finally {
      setSubmitting(false);
    }
  };

  const serviceTypeLabels = {
    purchase: "Fahrzeugkauf",
    repair: "Reparatur",
    inspection: "Inspektion/Wartung",
    general: "Allgemein"
  };

  const StarRating = ({ rating, interactive = false, onRate = () => {} }) => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type={interactive ? "button" : undefined}
          onClick={() => interactive && onRate(star)}
          className={interactive ? "cursor-pointer hover:scale-110 transition-transform" : "cursor-default"}
          disabled={!interactive}
        >
          <Star
            className={`w-6 h-6 ${
              star <= rating
                ? "fill-[#EFDF00] text-[#EFDF00]"
                : "fill-[#D9D9D6] text-[#D9D9D6]"
            }`}
          />
        </button>
      ))}
    </div>
  );

  if (submitted) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="w-20 h-20 bg-green-100  flex items-center justify-center mx-auto mb-8">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-black  mb-4">
            Vielen Dank für Ihre Bewertung!
          </h1>
          <p className="text-[#888B8D] mb-8">
            Ihre Bewertung wird nach Prüfung durch unser Team veröffentlicht.
          </p>
          <Button 
            onClick={() => {
              setSubmitted(false);
              setShowForm(false);
              setFormData({
                customer_name: "",
                email: "",
                rating: 5,
                title: "",
                text: "",
                service_type: "general",
                vehicle_info: ""
              });
            }}
            className="bg-[#000000] text-white hover:bg-[#1a1a1a"
          >
            Zurück zu den Bewertungen
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-[#000000] py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white  mb-4">
            Kundenbewertungen
          </h1>
          <p className="text-[#D9D9D6] text-lg max-w-2xl mx-auto">
            Erfahren Sie, was unsere Kunden über Auto Thurm sagen
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-center gap-8">
            {/* Average Rating */}
            <div className="text-center">
              <div className="text-6xl font-bold text-black ">
                {stats.combined_average || stats.average || "-"}
              </div>
              <StarRating rating={Math.round(stats.combined_average || stats.average || 0)} />
              <p className="text-[#888B8D] mt-2">{stats.total} Bewertungen</p>
              
              {/* Google & Local Split */}
              {(stats.google_count > 0 || stats.local_count > 0) && (
                <div className="mt-3 flex gap-4 justify-center text-xs">
                  {stats.google_count > 0 && (
                    <span className="flex items-center gap-1 text-[#4285F4]">
                      <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      {stats.google_count} Google ({stats.google_average})
                    </span>
                  )}
                  {stats.local_count > 0 && (
                    <span className="text-[#888B8D]">
                      {stats.local_count} Direkt ({stats.local_average})
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Google Place Info */}
            {placeInfo && placeInfo.google_maps_url && (
              <div className="text-center p-4 bg-[#F5F5F5] rounded-lg">
                <div className="flex items-center gap-2 justify-center mb-2">
                  <svg className="w-5 h-5 text-[#4285F4]" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  <span className="font-semibold text-sm">Google Bewertungen</span>
                </div>
                <a 
                  href={placeInfo.google_maps_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#4285F4] text-sm hover:underline flex items-center gap-1 justify-center"
                >
                  <MapPin className="w-3 h-3" />
                  Auf Google Maps ansehen
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}

            {/* Write Review Button */}
            <div>
              <Button 
                onClick={() => setShowForm(!showForm)}
                className="bg-[#EFDF00] text-black hover:bg-[#EFDF00 font-semibold px-6"
                data-testid="write-review-btn"
              >
                <Star className="w-4 h-4 mr-2" />
                Bewertung schreiben
              </Button>
            </div>
          </div>

          {/* Filter Tabs */}
          {(googleReviews.length > 0 || reviews.length > 0) && (
            <div className="flex justify-center gap-2 mt-8">
              <Button
                variant={activeTab === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setActiveTab("all")}
                className={activeTab === "all" ? "bg-black text-white" : ""}
              >
                Alle ({stats.total})
              </Button>
              {reviews.length > 0 && (
                <Button
                  variant={activeTab === "local" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setActiveTab("local")}
                  className={activeTab === "local" ? "bg-black text-white" : ""}
                >
                  Direkte ({stats.local_count})
                </Button>
              )}
              {googleReviews.length > 0 && (
                <Button
                  variant={activeTab === "google" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setActiveTab("google")}
                  className={activeTab === "google" ? "bg-[#4285F4] text-white" : ""}
                >
                  <svg className="w-3 h-3 mr-1" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  </svg>
                  Google ({stats.google_count})
                </Button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Review Form */}
      {showForm && (
        <section className="py-8 bg-[#F5F5F5] border-b">
          <div className="max-w-2xl mx-auto px-4">
            <Card className="border-0 shadow-none">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold text-black  mb-6">
                  Ihre Bewertung
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Rating */}
                  <div>
                    <Label className="mb-2 block">Ihre Bewertung *</Label>
                    <StarRating 
                      rating={formData.rating} 
                      interactive={true}
                      onRate={(rating) => setFormData(p => ({ ...p, rating }))}
                    />
                  </div>

                  {/* Name & Email */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Ihr Name *</Label>
                      <Input 
                        value={formData.customer_name}
                        onChange={(e) => setFormData(p => ({ ...p, customer_name: e.target.value }))}
                        placeholder="Max Mustermann"
                        required
                      />
                    </div>
                    <div>
                      <Label>E-Mail (optional)</Label>
                      <Input 
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                        placeholder="max@beispiel.de"
                      />
                    </div>
                  </div>

                  {/* Service Type & Vehicle */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Art der Leistung</Label>
                      <Select 
                        value={formData.service_type}
                        onValueChange={(value) => setFormData(p => ({ ...p, service_type: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">Allgemein</SelectItem>
                          <SelectItem value="purchase">Fahrzeugkauf</SelectItem>
                          <SelectItem value="repair">Reparatur</SelectItem>
                          <SelectItem value="inspection">Inspektion/Wartung</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Fahrzeug (optional)</Label>
                      <Input 
                        value={formData.vehicle_info}
                        onChange={(e) => setFormData(p => ({ ...p, vehicle_info: e.target.value }))}
                        placeholder="z.B. Renault Clio 2024"
                      />
                    </div>
                  </div>

                  {/* Title */}
                  <div>
                    <Label>Titel Ihrer Bewertung *</Label>
                    <Input 
                      value={formData.title}
                      onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))}
                      placeholder="z.B. Super Service, sehr zufrieden!"
                      required
                    />
                  </div>

                  {/* Text */}
                  <div>
                    <Label>Ihre Erfahrung *</Label>
                    <Textarea 
                      value={formData.text}
                      onChange={(e) => setFormData(p => ({ ...p, text: e.target.value }))}
                      placeholder="Beschreiben Sie Ihre Erfahrung mit Auto Thurm..."
                      rows={4}
                      required
                    />
                  </div>

                  <div className="flex gap-3">
                    <Button 
                      type="submit" 
                      disabled={submitting}
                      className="bg-[#000000] text-white hover:bg-[#1a1a1a"
                    >
                      {submitting ? "Wird gesendet..." : "Bewertung absenden"}
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={() => setShowForm(false)}
                    >
                      Abbrechen
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Reviews List */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4">
          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-2 border-[#EFDF00] border-t-transparent  animate-spin mx-auto"></div>
            </div>
          ) : (reviews.length === 0 && googleReviews.length === 0) ? (
            <div className="text-center py-12">
              <Quote className="w-12 h-12 text-[#D9D9D6] mx-auto mb-4" />
              <p className="text-[#888B8D]">Noch keine Bewertungen vorhanden.</p>
              <p className="text-[#888B8D] text-sm">Seien Sie der Erste, der eine Bewertung schreibt!</p>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2">
              {/* Lokale Reviews */}
              {(activeTab === "all" || activeTab === "local") && reviews.map((review) => (
                <Card 
                  key={review.id} 
                  className={`border-0 shadow-none hover:shadow-none transition-shadow ${
                    review.is_featured ? 'ring-2 ring-[#EFDF00]' : ''
                  }`}
                >
                  <CardContent className="p-6">
                    {review.is_featured && (
                      <div className="text-xs text-[#EFDF00] font-semibold mb-2 flex items-center gap-1">
                        <ThumbsUp className="w-3 h-3" />
                        Empfohlen
                      </div>
                    )}
                    
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-bold text-black">{review.title || review.author_name}</h3>
                        <p className="text-sm text-[#888B8D]">{review.customer_name || review.author_name}</p>
                      </div>
                      <StarRating rating={review.rating} />
                    </div>

                    <p className="text-[#888B8D] mb-4">{review.text}</p>

                    <div className="flex items-center justify-between text-xs text-[#888B8D]">
                      <span className="bg-[#EBEBEB] px-2 py-1">
                        {serviceTypeLabels[review.service_type] || review.service_type || "Allgemein"}
                      </span>
                      {review.vehicle_info && (
                        <span>{review.vehicle_info}</span>
                      )}
                      <span>
                        {review.created_at ? new Date(review.created_at).toLocaleDateString('de-DE') : ''}
                      </span>
                    </div>

                    {review.admin_response && (
                      <div className="mt-4 p-3 bg-[#F5F5F5]  border-l-4 border-[#EFDF00]">
                        <p className="text-xs font-semibold text-black mb-1">Antwort von Auto Thurm:</p>
                        <p className="text-sm text-[#888B8D]">{review.admin_response}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}

              {/* Google Reviews */}
              {(activeTab === "all" || activeTab === "google") && googleReviews.map((review) => (
                <Card 
                  key={review.id} 
                  className="border-0 shadow-none hover:shadow-none transition-shadow border-l-4 border-l-[#4285F4]"
                >
                  <CardContent className="p-6">
                    <div className="text-xs text-[#4285F4] font-semibold mb-2 flex items-center gap-1">
                      <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      </svg>
                      Google Bewertung
                    </div>
                    
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {review.author_photo && (
                          <img 
                            src={review.author_photo} 
                            alt={review.author_name}
                            className="w-10 h-10 rounded-full"
                          />
                        )}
                        <div>
                          <h3 className="font-bold text-black">{review.author_name}</h3>
                          <p className="text-xs text-[#888B8D]">{review.relative_time}</p>
                        </div>
                      </div>
                      <StarRating rating={review.rating} />
                    </div>

                    <p className="text-[#888B8D] mb-4">{review.text}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ReviewsPage;
